// Mantine
import { Badge, Portal, Loader, Pill } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'

// React
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'

// Router
import { Link, useNavigate, useLocation } from 'react-router-dom'

// Bus
import { useListener } from 'react-bus'

// Search
import useSearch from '../../hooks/useSearch'

// Date formatting
import dayjs from '../../lib/dayjs'

// Icons (unified on @untitled-ui/icons-react)
import {
    Container, Tag01, ShoppingBag01, Package, Clock,
    LogIn01, CreditCard01, ShoppingCart01, Tool01,
    File06, Receipt, Truck01,
    SearchMd, XClose, ChevronRight, ArrowLeft,
} from '@untitled-ui/icons-react'

// Classnames
import classNames from 'classnames'

// Status labels
import productionOrderStatuses from '../../lib/productionOrderStatuses'
import { getServiceCaseStatus } from '../../lib/serviceCaseStatuses'
import { getEstimateStatus, getInvoiceStatus } from '../../lib/retailerStatuses'
import { orderFinancialLabels, orderFulfillmentLabels } from '../../lib/orderStatuses'
import { statuses as shipmentStatuses, serviceCodes as shipmentServiceCodes } from '../../lib/shipment'
import { statusName as purchaseOrderStatusName, statusDotColor as purchaseOrderDotColor } from '../purchase_orders/purchaseOrderStatuses'

// Recent searches helpers
import { getRecentSearches, addRecentSearch, removeRecentSearch } from '../../lib/recentSearches'

// Section definitions — index doubles as the unique key
const SECTIONS = [
    { index: 'components', label: 'Composants', icon: Container, getUrl: (hit) => '/components/' + hit.id },
    { index: 'shopify_products', label: 'Produits Shopify', icon: Tag01, getUrl: (hit) => '/products/shopify/' + hit.id },
    { index: 'orders', label: 'Commandes', icon: ShoppingBag01, getUrl: (hit) => '/orders/' + hit.id },
    { index: 'fulfillment_orders', label: 'Préparations', icon: Package, getUrl: (hit) => '/fulfillment_orders/' + hit.id },
    { index: 'production_orders', label: 'Assemblages', icon: Clock, getUrl: (hit) => '/assembly/' + hit.id },
    { index: 'purchase_orders', label: 'Bons de commande', icon: LogIn01, getUrl: (hit) => '/purchase_orders/' + hit.id },
    { index: 'warranties', label: 'Garanties', icon: CreditCard01, getUrl: (hit) => '/warranties/' + hit.code },
    { index: 'retailers', label: 'Revendeurs', icon: ShoppingCart01, getUrl: (hit) => '/retailers/' + hit.id },
    { index: 'service_cases', label: 'SAV', icon: Tool01, getUrl: (hit) => '/service-cases/' + hit.id },
    { index: 'retailer_estimates', label: 'Devis revendeurs', icon: File06, getUrl: (hit) => '/retailer_estimates/' + hit.id },
    { index: 'retailer_invoices', label: 'Factures revendeurs', icon: Receipt, getUrl: (hit) => '/retailer_invoices/' + hit.id },
    { index: 'shipments', label: 'Expéditions', icon: Truck01, getUrl: (hit) => hit.fulfillment_order_id ? '/fulfillment_orders/' + hit.fulfillment_order_id : '/' },
]

const MAX_HITS_PER_SECTION = 3

// Fulfillment order status labels (no dedicated lib)
const foStatusLabels = {
    OPEN: { label: 'Ouvert', color: 'text-blue-500' },
    IN_PROGRESS: { label: 'En cours', color: 'text-orange-500' },
    CLOSED: { label: 'Fermé', color: 'text-green-500' },
}

// Map Mantine badge color names to Tailwind text colors
const badgeColorToText = { orange: 'text-orange-500', blue: 'text-blue-500', green: 'text-green-500', red: 'text-red-500', pink: 'text-pink-500', gray: 'text-gray-400', yellow: 'text-yellow-500' }

// Shared meta display components
const MetaStack = ({ children }) => (
    <div className="flex flex-col items-end gap-0.5">{children}</div>
)

const MetaLabel = ({ color, children }) => (
    <span className={classNames('text-[11px]', color || 'text-gray-400')}>{children}</span>
)

const MetaDate = ({ children }) => (
    <span className="text-gray-400 text-[11px]">{children}</span>
)

// Right-side info per section
export function HitMeta({ index, hit }) {
    switch (index) {
        case 'components':
            return hit.is_archived ? <Badge size="xs" color="dark">Archivé</Badge> : null

        case 'shopify_products':
            if (hit.image_url) {
                let src = hit.image_url
                if (src.startsWith('https://cdn.shopify.com/')) {
                    src = src.replace('https://cdn.shopify.com/', 'https://balticwatches-6242.imgix.net/') + '&auto=format,compress&w=80'
                }
                return <img className="h-[40px] aspect-square object-contain" src={src} alt="" />
            }
            return null

        case 'orders': {
            const fin = orderFinancialLabels[hit.order_financial_status]
            const ful = orderFulfillmentLabels[hit.order_fulfillment_status]
            return (
                <MetaStack>
                    {(fin || ful) && (
                        <span className="text-[11px] flex items-center gap-1">
                            {fin && <span className={fin.color}>{fin.label}</span>}
                            {fin && ful && <span className="text-gray-300">·</span>}
                            {ful && <span className={ful.color}>{ful.label}</span>}
                        </span>
                    )}
                    {hit.created_at && <MetaDate>{dayjs.unix(hit.created_at).fromNow()}</MetaDate>}
                </MetaStack>
            )
        }

        case 'fulfillment_orders': {
            const fo = foStatusLabels[hit.status]
            return (
                <MetaStack>
                    {hit.status && <MetaLabel color={fo?.color}>{fo?.label ?? hit.status}</MetaLabel>}
                    {hit.created_at && <MetaDate>{dayjs.unix(hit.created_at).fromNow()}</MetaDate>}
                </MetaStack>
            )
        }

        case 'production_orders': {
            const po = productionOrderStatuses({ status: hit.status })
            return (
                <MetaStack>
                    {hit.status && <MetaLabel color={badgeColorToText[po.badgeColor]}>{po.label}</MetaLabel>}
                    {hit.ending_at && <MetaDate>{dayjs(hit.starting_at).format('D MMM YYYY')} → {dayjs(hit.ending_at).format('D MMM YYYY')}</MetaDate>}
                </MetaStack>
            )
        }

        case 'purchase_orders': {
            const color = badgeColorToText[purchaseOrderDotColor(hit.status)]
            return (
                <MetaStack>
                    {hit.status && <MetaLabel color={color}>{purchaseOrderStatusName(hit.status) || hit.status}</MetaLabel>}
                    {hit.reception_at && <MetaDate>{dayjs(hit.reception_at).format('D MMM YYYY')}</MetaDate>}
                </MetaStack>
            )
        }

        case 'warranties':
            if (hit.warranty_start_at || hit.warranty_end_at) {
                return <MetaDate>{dayjs(hit.warranty_start_at).format('D MMM YYYY')} → {dayjs(hit.warranty_end_at).format('D MMM YYYY')}</MetaDate>
            }
            return null

        case 'service_cases': {
            const sc = getServiceCaseStatus(hit.status)
            const scDate = hit.current_status_at || hit.created_at
            return (
                <MetaStack>
                    {hit.is_archived && <Badge size="xs" color="dark">Archivé</Badge>}
                    {hit.status && <MetaLabel color={sc.classes?.text}>{sc.label}</MetaLabel>}
                    {scDate && <MetaDate>{dayjs.unix(scDate).fromNow()}</MetaDate>}
                </MetaStack>
            )
        }

        case 'retailer_estimates': {
            const es = getEstimateStatus(hit.status)
            return (
                <MetaStack>
                    {hit.amount && <span className="text-gray-500 text-[11px] font-medium">{hit.amount}</span>}
                    {hit.status && <MetaLabel color={es.textColor}>{es.label}</MetaLabel>}
                    {hit.date && <MetaDate>{dayjs.unix(hit.date).fromNow()}</MetaDate>}
                </MetaStack>
            )
        }

        case 'retailer_invoices': {
            const inv = getInvoiceStatus(hit.status)
            return (
                <MetaStack>
                    {hit.amount && <span className="text-gray-500 text-[11px] font-medium">{hit.amount}</span>}
                    {hit.status && <MetaLabel color={inv.textColor}>{inv.label}</MetaLabel>}
                    {hit.date && <MetaDate>{dayjs.unix(hit.date).fromNow()}</MetaDate>}
                </MetaStack>
            )
        }

        case 'shipments': {
            const ts = shipmentStatuses[hit.tracking_status] ?? shipmentStatuses.unknown
            return (
                <MetaStack>
                    {hit.service && <MetaDate>{shipmentServiceCodes[hit.service]?.label ?? hit.service}</MetaDate>}
                    {hit.tracking_status && <MetaLabel color={ts.textColor}>{ts.label}</MetaLabel>}
                </MetaStack>
            )
        }

        default:
            return null
    }
}

// Section header (shared between overview and expanded)
function SectionHeader({ label, count, expandable, onExpand }) {
    const Tag = expandable ? 'button' : 'div'
    return (
        <Tag
            className={classNames(
                'flex items-center justify-between px-4 sm:px-5 pt-3 pb-1.5 w-full',
                expandable && 'group cursor-pointer',
            )}
            onClick={expandable ? onExpand : undefined}
        >
            <span className={classNames(
                'text-[11px] font-semibold text-gray-400 uppercase tracking-wider',
                expandable && 'group-hover:text-gray-600 transition-colors',
            )}>
                {label}
            </span>
            <span className={classNames(
                'flex items-center gap-1',
                expandable && 'text-[11px] text-gray-400 group-hover:text-gray-600 transition-colors',
            )}>
                <Pill size="xs">{count}</Pill>
                {expandable && <ChevronRight className="w-3.5 h-3.5" />}
            </span>
        </Tag>
    )
}

export default function TypesenseGlobalSearch() {
    const { multiSearch, isReady } = useSearch()
    const [searchQuery, setSearchQuery] = useState('')
    const [results, setResults] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [modalOpened, setModalOpened] = useState(false)
    const [activeHitIndex, setActiveHitIndex] = useState(-1)
    const [recentSearches, setRecentSearches] = useState([])

    // Expanded section mode: show all results for one section
    const [expandedSection, setExpandedSection] = useState(null)
    const [expandedHits, setExpandedHits] = useState([])
    const [expandedTotal, setExpandedTotal] = useState(0)
    const [isLoadingExpanded, setIsLoadingExpanded] = useState(false)

    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300)

    const inputRef = useRef(null)
    const resultsContainerRef = useRef(null)
    const navigate = useNavigate()
    const location = useLocation()

    // Build sections with results, sorted by relevance (topScore)
    const sections = useMemo(() => {
        if (!results || (Array.isArray(results) && !results.length) || !Object.keys(results).length) return []
        return SECTIONS.map((section) => {
            const data = results[section.index]
            return {
                ...section,
                hits: (data?.hits ?? []).slice(0, MAX_HITS_PER_SECTION),
                nbHits: data?.nbHits ?? 0,
                topScore: data?.topScore ?? 0,
            }
        }).filter((s) => s.nbHits > 0)
          .sort((a, b) => b.topScore - a.topScore)
    }, [results])

    // Flat list of all visible hits for keyboard navigation
    const flatHits = useMemo(() => {
        if (expandedSection) {
            const section = SECTIONS.find((s) => s.index === expandedSection)
            return expandedHits.map((hit) => ({ hit, section }))
        }
        return sections.flatMap((section) =>
            section.hits.map((hit) => ({ hit, section }))
        )
    }, [sections, expandedSection, expandedHits])

    // Collapse back to overview
    const collapseSection = useCallback(() => {
        setExpandedSection(null)
        setExpandedHits([])
        setExpandedTotal(0)
        setActiveHitIndex(-1)
    }, [])

    // Expand a section: re-query with higher hitsPerPage
    const expandSection = useCallback(async (slug) => {
        const section = SECTIONS.find((s) => s.index === slug)
        if (!section || !debouncedSearchQuery) return

        setExpandedSection(slug)
        setIsLoadingExpanded(true)
        setActiveHitIndex(-1)

        try {
            const { results: res } = await multiSearch(debouncedSearchQuery, [section.index], 50)
            const data = res[section.index]
            setExpandedHits(data?.hits ?? [])
            setExpandedTotal(data?.nbHits ?? 0)
        } catch {
            setExpandedHits([])
            setExpandedTotal(0)
        } finally {
            setIsLoadingExpanded(false)
        }
    }, [debouncedSearchQuery, multiSearch])

    // Open modal
    const openModal = useCallback(() => {
        setModalOpened(true)
        setSearchQuery('')
        setResults([])
        setActiveHitIndex(-1)
        collapseSection()
        setRecentSearches(getRecentSearches())
        setTimeout(() => inputRef.current?.focus(), 50)
    }, [collapseSection])

    // Close modal — content stays visible during the 200ms fade-out animation
    const closeModal = useCallback(() => {
        setModalOpened(false)
        setActiveHitIndex(-1)
        setIsLoading(false)
    }, [])

    // Listen for bus event
    useListener('openGlobalSearch', openModal)

    // Cmd+K / Ctrl+K shortcut
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                modalOpened ? closeModal() : openModal()
            }
            if (e.key === 'Escape' && modalOpened) {
                closeModal()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [modalOpened, openModal, closeModal])

    // Search on debounced query change
    useEffect(() => {
        if (debouncedSearchQuery && isReady) {
            setIsLoading(true)
            collapseSection()
            const indexes = SECTIONS.map((s) => s.index)
            multiSearch(debouncedSearchQuery, indexes, MAX_HITS_PER_SECTION).then(({ results }) => {
                setResults(results)
                setIsLoading(false)
                setActiveHitIndex(-1)
                addRecentSearch(debouncedSearchQuery)
            })
        } else {
            setResults([])
            setIsLoading(false)
            setActiveHitIndex(-1)
            collapseSection()
        }
    }, [debouncedSearchQuery, isReady])

    // Show loading immediately on typing
    useEffect(() => {
        if (searchQuery && searchQuery !== debouncedSearchQuery) {
            setIsLoading(true)
        }
    }, [searchQuery, debouncedSearchQuery])

    // Close on route change
    useEffect(() => {
        closeModal()
    }, [location.key])

    // Scroll active hit into view
    useEffect(() => {
        if (activeHitIndex < 0 || !resultsContainerRef.current) return
        const items = resultsContainerRef.current.querySelectorAll('[data-hit]')
        items[activeHitIndex]?.scrollIntoView({ block: 'nearest' })
    }, [activeHitIndex])

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveHitIndex((prev) => (prev < flatHits.length - 1 ? prev + 1 : prev))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveHitIndex((prev) => (prev > 0 ? prev - 1 : -1))
        } else if (e.key === 'Enter') {
            if (activeHitIndex >= 0 && flatHits[activeHitIndex]) {
                e.preventDefault()
                const { hit, section } = flatHits[activeHitIndex]
                navigate(section.getUrl(hit))
                closeModal()
            }
        } else if (e.key === 'Escape' && expandedSection) {
            e.preventDefault()
            e.stopPropagation()
            collapseSection()
        }
    }

    // State helpers
    const hasResults = expandedSection ? expandedHits.length > 0 : sections.length > 0
    const hasSearchedButNoResults = debouncedSearchQuery.length > 0 && !hasResults && !isLoading && !isLoadingExpanded
    const showEmptyState = !searchQuery && !hasResults && recentSearches.length > 0 && !expandedSection
    const hasContent = hasResults || showEmptyState || hasSearchedButNoResults || isLoading || isLoadingExpanded

    // Render a hit row (shared between overview and expanded)
    const renderHit = (hit, section, hitIndex) => {
        const url = section.getUrl(hit)
        const referenceRaw = Array.isArray(hit.reference) ? hit.reference.join(', ') : hit.reference
        const hasReference = referenceRaw && referenceRaw !== '[]'
        const isActive = hitIndex === activeHitIndex
        const SectionIcon = section.icon

        return (
            <div key={section.index + '-' + hit.id} data-hit>
                <Link
                    to={url}
                    className={classNames(
                        'flex items-center gap-3 px-4 sm:px-5 py-2.5 transition-colors',
                        isActive ? 'bg-gray-50' : 'hover:bg-gray-50/60',
                    )}
                    onMouseEnter={() => setActiveHitIndex(hitIndex)}
                    onClick={() => closeModal()}
                >
                    {SectionIcon && (
                        <SectionIcon className="shrink-0 w-4 h-4 text-gray-400" />
                    )}
                    <div className="flex flex-col min-w-0 flex-1 highlight-results-em">
                        <span
                            className="text-[13px] text-gray-800 truncate leading-snug"
                            dangerouslySetInnerHTML={{
                                __html: hit._highlightResult?.name?.value ?? hit.name,
                            }}
                        />
                        {hasReference && (
                            <span
                                className="text-[11px] text-gray-400 truncate leading-snug"
                                dangerouslySetInnerHTML={{
                                    __html: hit._highlightResult?.reference?.value || referenceRaw,
                                }}
                            />
                        )}
                    </div>
                    <div className="shrink-0">
                        <HitMeta index={section.index} hit={hit} />
                    </div>
                </Link>
            </div>
        )
    }

    return (
        <Portal target="#global-search-modal">
            {/* Backdrop */}
            <div
                className={classNames(
                    'fixed inset-0 z-9999 bg-black transition-opacity duration-200',
                    {
                        'opacity-60 pointer-events-auto': modalOpened,
                        'opacity-0 pointer-events-none': !modalOpened,
                    }
                )}
                onClick={closeModal}
            />

            {/* Modal */}
            <div
                className={classNames(
                    'fixed inset-0 z-9999 flex items-start justify-center pointer-events-none transition-all duration-200',
                    { 'opacity-100': modalOpened, 'opacity-0': !modalOpened }
                )}
            >
                <div
                    className={classNames(
                        'relative flex flex-col bg-white w-full transition-all duration-200 origin-top',
                        hasContent ? 'h-dvh sm:h-auto' : 'sm:h-auto',
                        'sm:rounded-xl sm:mt-[min(12vh,120px)] sm:max-w-[600px] sm:mx-5 sm:max-h-[70vh]',
                        {
                            'pointer-events-auto scale-100 translate-y-0': modalOpened,
                            'pointer-events-none scale-95 -translate-y-2': !modalOpened,
                        }
                    )}
                >
                    {/* Header / Input */}
                    <div className={classNames(
                        'flex items-center gap-3 px-4 sm:px-5 py-3 shrink-0',
                        { 'border-b': hasResults || showEmptyState || hasSearchedButNoResults }
                    )}>
                        {expandedSection ? (
                            <button
                                className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={collapseSection}
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        ) : (
                            <SearchMd className="shrink-0 w-5 h-5 text-gray-400" />
                        )}
                        <input
                            ref={inputRef}
                            type="text"
                            className="flex-1 appearance-none bg-transparent outline-hidden text-base placeholder:text-gray-400"
                            placeholder="Rechercher..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            id="global-search-input"
                        />
                        {(isLoading || isLoadingExpanded) && <Loader size={16} />}
                        <button
                            className="sm:hidden p-1 text-gray-400 hover:text-gray-600"
                            onClick={closeModal}
                        >
                            <XClose className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Recent searches */}
                    {showEmptyState && (
                        <div className="px-4 sm:px-5 py-4">
                            <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">
                                Recherches récentes
                            </div>
                            <div className="flex flex-col">
                                {recentSearches.map((query) => (
                                    <div
                                        key={query}
                                        className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-gray-50 group cursor-pointer"
                                        onClick={() => {
                                            setSearchQuery(query)
                                            inputRef.current?.focus()
                                        }}
                                    >
                                        <Clock className="shrink-0 w-4 h-4 text-gray-300" />
                                        <span className="text-sm text-gray-600 flex-1">{query}</span>
                                        <button
                                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-gray-500 transition-opacity"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                removeRecentSearch(query)
                                                setRecentSearches(getRecentSearches())
                                            }}
                                        >
                                            <XClose className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No results */}
                    {hasSearchedButNoResults && (
                        <div className="text-gray-500 text-sm text-center py-16">
                            Aucun résultat pour &laquo; {debouncedSearchQuery} &raquo;
                        </div>
                    )}

                    {/* Expanded section view — all results for one section */}
                    {expandedSection && (hasResults || isLoadingExpanded) && (() => {
                        const section = SECTIONS.find((s) => s.index === expandedSection)
                        return (
                            <div ref={resultsContainerRef} className="overflow-y-auto flex-1 sm:max-h-[50vh]">
                                <SectionHeader label={section.label} count={expandedTotal} />
                                {expandedHits.map((hit, i) => renderHit(hit, section, i))}
                            </div>
                        )
                    })()}

                    {/* Results — grouped sections (overview) */}
                    {!expandedSection && hasResults && (
                        <div ref={resultsContainerRef} className="overflow-y-auto flex-1 sm:max-h-[50vh]">
                            {sections.map((section, sectionIdx) => {
                                const offset = sections.slice(0, sectionIdx).reduce((sum, s) => sum + s.hits.length, 0)
                                const expandable = section.nbHits > MAX_HITS_PER_SECTION
                                return (
                                    <div key={section.index}>
                                        <SectionHeader
                                            label={section.label}
                                            count={section.nbHits}
                                            expandable={expandable}
                                            onExpand={() => expandSection(section.index)}
                                        />
                                        {section.hits.map((hit, i) => renderHit(hit, section, offset + i))}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Footer */}
                    {(hasResults || showEmptyState) && (
                        <div className="hidden sm:flex items-center gap-4 px-5 py-2.5 border-t text-xs text-gray-400 shrink-0">
                            <span className="flex items-center gap-1">
                                <kbd className="bg-gray-100 px-1 py-0.5 rounded-sm text-[10px]">↑↓</kbd>
                                naviguer
                            </span>
                            <span className="flex items-center gap-1">
                                <kbd className="bg-gray-100 px-1 py-0.5 rounded-sm text-[10px]">↵</kbd>
                                ouvrir
                            </span>
                            <span className="flex items-center gap-1">
                                <kbd className="bg-gray-100 px-1 py-0.5 rounded-sm text-[10px]">Esc</kbd>
                                {expandedSection ? 'retour' : 'fermer'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    )
}
